import React, {memo} from "react";
import PropTypes from "prop-types";
import {
  CartesianGrid,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  ReferenceArea,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import humanizeString from "string-humanize";
import ViewLoadingSpinner from "../../Containers/Cell/View/ViewLoader";
import ZoomOutMessageContainer from "../../Containers/ZoomOutMessageContainer";
import {useHandleChartData} from "../../Helpers/useHandleChartData";

/** This component will generate Plot of type XY.
 *
 * This component uses LineChart component of recharts library : https://recharts.org/en-US/api/LineChart
 */

const XYPlot = (props) => {
  const {properties} = props;
  const {axes = {}, legend = {}, line_keys: lines = [], xColumn} = properties;
  const {hide: legendHide = false} = legend;
  const xAxis = axes.x || {};
  const yAxis = axes.y || [];

  const {
    colorHexes,
    formatValue,
    isLoading,
    setXDomainLeft,
    setXDomainRight,
    showMessage,
    timeOnXAxis,
    timeOnYAxis,
    visibleData,
    xDomainLeft,
    xDomainRight,
    zoomIn,
    zoomOut,
  } = useHandleChartData(props);

  return (
    <ZoomOutMessageContainer showMessage={showMessage}>
      {isLoading ? (
        <ViewLoadingSpinner />
      ) : (
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={visibleData}
            onDoubleClick={zoomOut}
            onMouseDown={(nextState = {}) => setXDomainLeft(nextState.activeLabel || "")}
            onMouseMove={(nextState = {}) =>
              xDomainLeft && setXDomainRight(nextState.activeLabel || "")
            }
            onMouseUp={zoomIn}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              allowDataOverflow={true}
              dataKey={xColumn}
              domain={["auto", "auto"]}
              scale={timeOnXAxis ? "time" : undefined}
              tick={{fontSize: 10, fontFamily: "Roboto, Arial"}}
              tickFormatter={(value) => formatValue(value, "x", xAxis)}
              type={timeOnXAxis ? "number" : undefined}
            />
            {yAxis.map((yAxisItem, index) => {
              const lineIndex = lines.findIndex((line) => line.yAxisId === yAxisItem.id);
              const axisColor = lineIndex !== -1 ? colorHexes[lineIndex] : undefined;
              return (
                <YAxis
                  domain={["auto", "auto"]}
                  key={yAxisItem.id}
                  orientation={index % 2 === 1 ? "right" : "left"}
                  scale={timeOnYAxis ? "time" : undefined}
                  stroke={axisColor}
                  tick={{fontSize: 10, fontFamily: "Roboto, Arial", fill: axisColor}}
                  tickFormatter={(value) => formatValue(value, "y", yAxisItem)}
                  type={timeOnYAxis ? "number" : undefined}
                  yAxisId={yAxisItem.id}
                />
              );
            })}
            <Tooltip
              formatter={(value) => formatValue(value, "y", yAxis[0])}
              labelFormatter={(label) => formatValue(label, "x", xAxis)}
            />
            {!legendHide && <Legend />}
            {lines.map(({key, yAxisId}, index) => {
              if (!key) return null;
              return (
                <Line
                  animationDuration={300}
                  dataKey={key}
                  key={key}
                  name={humanizeString(key.replaceAll(".", "_"))}
                  stroke={colorHexes[index]}
                  type="monotone"
                  yAxisId={yAxisId}
                />
              );
            })}
            {xDomainLeft && xDomainRight ? (
              <ReferenceArea
                yAxisId={yAxis[0] && yAxis[0].id}
                opacity={0.2}
                x1={xDomainLeft}
                x2={xDomainRight}
                fill="#006dd9"
              />
            ) : null}
          </LineChart>
        </ResponsiveContainer>
      )}
    </ZoomOutMessageContainer>
  );
};

XYPlot.propTypes = {
  properties: PropTypes.object.isRequired,
  jsonData: PropTypes.array.isRequired,
  timeZone: PropTypes.string,
};

export default memo(XYPlot);
